import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Coins, Loader2, Users, Check, Clock, Shield } from "lucide-react";

const HB_ICON = "https://legends.ml/static/images/lib/skill/1301007.png";
const HP_GEAR_ICON = "https://royals-library.netlify.app/images/skills/0001001.png";
const SAFE_RUN_ICON = "https://royals-library.netlify.app/images/skills/2321006.png";

export default function CreateWishModal({
  showCreateWish, setShowCreateWish, wishForm, setWishForm, userCharacters,
  handleCreateWish, actionLoading, expeditionBosses, formatMesos, availableJobs,
}) {
  const [selectedChars, setSelectedChars] = useState([]);
  
  const toggleChar = (char) => {
    setSelectedChars(prev => 
      prev.some(c => c.id === char.id)
        ? prev.filter(c => c.id !== char.id)
        : [...prev, char]
    );
  };

  const onSubmit = () => {
    // Attach selected characters to the form
    if (selectedChars.length > 0) {
      setWishForm(prev => ({ ...prev, characters: selectedChars.map(c => ({
        character_id: c.id, name: c.name, job: c.job, level: c.level
      }))}));
    }
    handleCreateWish(selectedChars.map(c => ({
      character_id: c.id, name: c.name, job: c.job, level: c.level
    })));
  };

  const hasChars = userCharacters && userCharacters.length > 0;

  return (
      <Dialog open={showCreateWish} onOpenChange={(v) => { setShowCreateWish(v); if (!v) setSelectedChars([]); }}>
        <DialogContent className="bg-slate-900 border-blue-500/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-blue-400">Wish to Join Expedition</DialogTitle>
            <DialogDescription className="text-blue-200/60">
              Let hunters know you're looking for an expedition
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-blue-200">Boss</Label>
              <Select value={wishForm.boss_id} onValueChange={(v) => setWishForm({ ...wishForm, boss_id: v })}>
                <SelectTrigger className="bg-black/40 border-gray-600">
                  <SelectValue placeholder="Select a boss" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {expeditionBosses.map((boss) => (
                    <SelectItem key={boss.id} value={boss.id}>{boss.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-blue-200">Reward Offered (M mesos)</Label>
              <div className="relative">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="e.g., 10"
                  value={wishForm.reward_offered}
                  onChange={(e) => setWishForm({ ...wishForm, reward_offered: e.target.value })}
                  className="bg-black/40 border-gray-600 pr-24"
                  data-testid="wish-reward-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-400/70 pointer-events-none">
                  {wishForm.reward_offered ? formatMesos(wishForm.reward_offered) : "M mesos"}
                </span>
              </div>
            </div>

            {/* What I Need section */}
            <div className="p-3 rounded-lg space-y-2" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
              <Label className="text-blue-300 text-sm font-semibold">What I Need from the Expedition</Label>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={wishForm.hb_required}
                  onCheckedChange={(c) => setWishForm({ ...wishForm, hb_required: c })}
                  className="data-[state=checked]:bg-pink-600 data-[state=checked]:border-pink-500"
                  data-testid="wish-hb-checkbox"
                />
                <img src={HB_ICON} alt="HB" className="w-5 h-5" />
                <span className="text-blue-200 text-sm">Hyper Body</span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={wishForm.hp_gear_required}
                  onCheckedChange={(c) => setWishForm({ ...wishForm, hp_gear_required: c })}
                  className="data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-500"
                  data-testid="wish-hp-gear-checkbox"
                />
                <img src={HP_GEAR_ICON} alt="HP Gear" className="w-5 h-5" />
                <span className="text-blue-200 text-sm">HP Gear</span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={wishForm.safe_run_required}
                  onCheckedChange={(c) => setWishForm({ ...wishForm, safe_run_required: c })}
                  className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-500"
                  data-testid="wish-safe-run-checkbox"
                />
                <img src={SAFE_RUN_ICON} alt="Safe Run" className="w-5 h-5" />
                <span className="text-blue-200 text-sm">Guaranteed Safe Run</span>
              </div>
            </div>

            {/* Character Selection */}
            {hasChars && (
              <div>
                <Label className="text-blue-200 mb-2 block">Select Character(s) to Join With</Label>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {userCharacters.map((char) => {
                    const isSelected = selectedChars.some(c => c.id === char.id);
                    const jobs = availableJobs || {};
                    const charSkills = char.skills || jobs[char.job]?.skills || [];
                    return (
                      <div
                        key={char.id}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${isSelected ? "ring-1 ring-blue-400 bg-blue-500/10" : "hover:bg-blue-500/5 bg-black/30"}`}
                        onClick={() => toggleChar(char)}
                        data-testid={`wish-char-${char.id}`}
                      >
                        <div className="flex items-center gap-2">
                          {charSkills.slice(0, 1).map((skill, si) => (
                            <img key={si} src={skill.icon} alt={skill.name} className="w-5 h-5" />
                          ))}
                          <div>
                            <p className="text-blue-100 text-sm font-medium">{char.name || char.job}</p>
                            <p className="text-[10px] text-blue-300/70">{char.job} - Lv.{char.level}</p>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-blue-500 border-blue-400" : "border-blue-500/40"}`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-blue-200">
                Contact IGN {selectedChars.length > 0 ? "(Optional)" : "*"}
              </Label>
              <Input
                placeholder="Your in-game name"
                value={wishForm.contact_ign}
                onChange={(e) => setWishForm({ ...wishForm, contact_ign: e.target.value })}
                className="bg-black/40 border-gray-600"
                data-testid="wish-contact-ign"
              />
              {selectedChars.length > 0 && !wishForm.contact_ign && (
                <p className="text-[10px] text-blue-400/50 mt-1">Character name will be used as contact</p>
              )}
            </div>

            <div>
              <Label className="text-blue-200">Queue Duration</Label>
              <Select value={String(wishForm.queue_duration_hours)} onValueChange={(v) => setWishForm({ ...wishForm, queue_duration_hours: parseInt(v) })}>
                <SelectTrigger className="bg-black/40 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-blue-200">Message (Optional)</Label>
              <Textarea
                placeholder="Anything else hunters should know..."
                value={wishForm.message}
                onChange={(e) => setWishForm({ ...wishForm, message: e.target.value })}
                className="bg-black/40 border-gray-600"
                rows={2}
              />
            </div>
          </div>

          {/* Quest Prerequisite Disclaimer */}
          <div className="p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
            <p className="text-xs text-yellow-300/90 leading-relaxed">
              <Shield className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-yellow-400" />
              Remember that you need to have done all pre-requisite quests for this expedition if any are necessary.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowCreateWish(false); setSelectedChars([]); }}>Cancel</Button>
            <Button className="bg-blue-500 hover:bg-blue-600 text-white" onClick={onSubmit}>
              Post Wish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
